import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { inputFieldMarginBottom } from "../../../../../../shared";
import Checkbox from "../../../../../CheckBox/Checkbox";
import InputField from "../../../../../InputField/InputField";
import { EditVehiclePayloadType } from "../../../../types";

interface IEditVehicleDialogContentProps {
  payload: EditVehiclePayloadType;
  nameErrorText: string | null;
  descriptionErrorText: string | null;
  isLoading: boolean;
  toggleVehicleDeactivate: () => void;
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImeiValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validateDescription: (event: any) => void;
  validateImei: (event: any) => void;
  validateName: (event: any) => void;
  imeiErrorText?: string | null;
  autoAssignVrnEnabled?: boolean;
}

const EditVehicleDialogContent = ({
  payload,
  nameErrorText,
  descriptionErrorText,
  isLoading,
  onNameChange,
  onImeiValueChange,
  onDescriptionChange,
  toggleVehicleDeactivate,
  validateDescription,
  validateImei,
  validateName,
  imeiErrorText,
  autoAssignVrnEnabled
}: IEditVehicleDialogContentProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const isVrnOptional = Boolean(autoAssignVrnEnabled);
  const optionalString = `(${t("Dialog##optional")})`;

  return (
    <>
      <InputField
        id={uuidv7()}
        data-testid="edit-vehicle-company-name"
        labelLeft={t("Dialog##company")}
        size="medium"
        placeholder={user?.selectedCompany.name}
        readOnly
        fullWidth
        customStyle={inputFieldMarginBottom}
      />
      <InputField
        id={uuidv7()}
        testId="edit-vehicle-number-input-field"
        size="medium"
        value={payload.name ?? ""}
        labelLeft={t("Dialog##vehicle number")}
        placeholder={t("Dialog##enter here")}
        fullWidth
        required
        infoTooltip={
          isVrnOptional
            ? t("Dialog##vehicle registration number tooltip")
            : undefined
        }
        errorText={nameErrorText}
        onBlur={validateName}
        onChange={onNameChange}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <InputField
        data-testid="edit-imei-input-field"
        id={uuidv7()}
        size="medium"
        value={payload.imei ?? ""}
        fullWidth
        errorText={imeiErrorText}
        onBlur={validateImei}
        onChange={onImeiValueChange}
        labelAddition={optionalString}
        labelLeft={t("Dialog##device imei")}
        placeholder={t("Dialog##imei placeholder")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <InputField
        id={uuidv7()}
        size="medium"
        data-testid="description-input-field"
        value={payload.comment ?? ""}
        fullWidth
        errorText={descriptionErrorText}
        onBlur={validateDescription}
        onChange={onDescriptionChange}
        labelLeft={t("Dialog##description required")}
        labelAddition={optionalString}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <Checkbox
        id={uuidv7()}
        label={t("Dialog##deactivate vehicle")}
        info={t("Dialog##deactivate vehicle description")}
        onChange={toggleVehicleDeactivate}
        checked={payload.disabled ?? false}
        disabled={isLoading}
      />
    </>
  );
};

export default EditVehicleDialogContent;
